<script setup lang="ts">
import { ref } from "vue"
import { useHttpClient } from "@/vf"
import { useShopDialogs } from "@/shop/composables/useShopDialogs.ts"
import type { AxiosError } from "axios"

const http = useHttpClient()
const dialogs = useShopDialogs()

const firstName = ref("")
const lastName = ref("")
const email = ref("")
const orderNumber = ref("")
const comment = ref("")

const submitted = ref(false)

const error = ref<string | undefined>(undefined)
const isSubmitting = ref(false)

async function revokeOrder() {
    const loadingDialog = dialogs.loading("Widerruf wird gesendet")

    if (isSubmitting.value) {
        return
    }

    isSubmitting.value = true

    try {
        await http.post(`/my-account/revoke-order`, {
            orderNumber: orderNumber.value,
            email: email.value,
            firstName: firstName.value,
            lastName: lastName.value,
            comment: comment.value,
        })
    } catch (e) {
        loadingDialog.ok()
        isSubmitting.value = false

        error.value = (e as AxiosError<{ error: string }>).response?.data?.error
        if (error.value === "invalid email") {
            return
        }

        await dialogs.alert("Fehler", "Der Widerruf konnte nicht gesendet werden. Bitte versuche es erneut.")
        return
    }

    loadingDialog.ok()
    isSubmitting.value = false
    submitted.value = true

    orderNumber.value = ""
    comment.value = ""
    firstName.value = ""
    lastName.value = ""
    email.value = ""
    error.value = undefined
}
</script>

<template>
    <form v-if="!submitted" @submit.prevent="revokeOrder">
        <p class="mb-4">Um eine Bestellung zu widerrufen, fülle bitte das folgende Formular aus.</p>
        <div class="row">
            <div class="col-lg-6">
                <div class="form-group">
                    <label for="firstName">Vorname</label>
                    <input id="firstName" v-model="firstName" type="text" class="form-control" />
                </div>
                <div class="form-group">
                    <label for="lastName">Nachname</label>
                    <input id="lastName" v-model="lastName" type="text" class="form-control" />
                </div>

                <div class="form-group mb-1">
                    <label for="email">E-Mail</label>
                    <input
                        id="email"
                        v-model="email"
                        class="form-control"
                        :class="{ 'is-invalid': error === 'invalid email' }"
                        type="email"
                        required
                    />
                </div>
                <div v-if="error === 'invalid email'" class="small text-danger mb-0 mt-0">
                    Bitte gebe eine gültige E-Mail-Adresse ein.
                </div>

                <div class="form-group mt-4">
                    <label for="orderNumber">Bestellnummer</label>
                    <input id="orderNumber" v-model="orderNumber" type="text" class="form-control" />
                </div>

                <div class="form-group">
                    <label for="comment">Kommentar hinzufügen</label>
                    <textarea
                        id="comment"
                        v-model="comment"
                        class="form-control"
                        rows="7"
                        placeholder="Wenn du nur einzelne Artikel deiner Bestellung widerrufen möchtest, kannst du uns das hier ebenfalls mitteilen."
                    ></textarea>
                </div>
            </div>
        </div>

        <!-- Buttons -->
        <div class="d-flex justify-content-between mt-3">
            <button class="btn btn-danger" type="submit">Widerruf bestätigen</button>
        </div>
    </form>

    <div v-if="submitted">
        <h2>Vielen Dank</h2>

        <p>
            Dein Widerruf wurde von uns erfasst und wird nun geprüft. Da unser System die Voraussetzungen eines
            Widerrufs nicht vorab bewertet, erfolgt die rechtliche Prüfung erst im Rahmen der Bearbeitung. Über das
            Ergebnis der Prüfung und die weiteren Schritte informieren wir dich gesondert per Mail.
        </p>
    </div>
</template>
